import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../../database/firebase';

const ApplicationsTrendChart = () => {
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [types, setTypes] = useState(['All']);
  const [statuses, setStatuses] = useState(['All']);
  const [industries, setIndustries] = useState(['All']);
  const [years, setYears] = useState(['All']);
  const [selectedType, setSelectedType] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedYear, setSelectedYear] = useState('All');

  useEffect(() => {
    const unsub = onSnapshot(collection(firestore, 'applications'), snapshot => {
      const allTypes = new Set();
      const allStatuses = new Set();
      const allIndustries = new Set();
      const allYears = new Set();
      snapshot.docs.forEach(doc => {
        const d = doc.data();
        if (d.type) allTypes.add(d.type);
        if (d.status) allStatuses.add(d.status);
        if (d.business && d.business.industry) allIndustries.add(d.business.industry);
        if (d.business && d.business.yearsInOperation) allYears.add(d.business.yearsInOperation);
      });
      setTypes(['All', ...Array.from(allTypes)]);
      setStatuses(['All', ...Array.from(allStatuses)]);
      setIndustries(['All', ...Array.from(allIndustries)]);
      setYears(['All', ...Array.from(allYears)]);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    setLoading(true);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const monthlyData = {};
    months.forEach(month => {
      monthlyData[month] = {
        month,
        commercial: 0,
        bif: 0,
      };
    });
    let q = collection(firestore, 'applications');
    const wheres = [];
    if (selectedType !== 'All') wheres.push(where('type', '==', selectedType));
    if (selectedStatus !== 'All') wheres.push(where('status', '==', selectedStatus));
    if (selectedIndustry !== 'All') wheres.push(where('business.industry', '==', selectedIndustry));
    if (selectedYear !== 'All') wheres.push(where('business.yearsInOperation', '==', selectedYear));
    if (wheres.length > 0) {
      wheres.forEach(w => {
        q = query(q, w);
      });
    }
    const unsubscribe = onSnapshot(q, snapshot => {
      months.forEach(month => {
        monthlyData[month].commercial = 0;
        monthlyData[month].bif = 0;
      });
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        let date = null;
        if (data.submittedAt && data.submittedAt.seconds) {
          date = new Date(data.submittedAt.seconds * 1000);
        } else if (data.createdAt && data.createdAt.seconds) {
          date = new Date(data.createdAt.seconds * 1000);
        } else if (data.submittedAt) {
          date = new Date(data.submittedAt);
        } else if (data.createdAt) {
          date = new Date(data.createdAt);
        }
        if (date) {
          const month = months[date.getMonth()];
          if ((data.type || '').toLowerCase() === 'bif') {
            monthlyData[month].bif++;
          } else {
            monthlyData[month].commercial++;
          }
        }
      });
      const sortedData = Object.values(monthlyData).sort((a, b) => {
        return months.indexOf(a.month) - months.indexOf(b.month);
      });
      setTrendData(sortedData);
      setLoading(false);
    });
    const darkListener = e => setDarkMode(e.matches);
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', darkListener);
    return () => {
      unsubscribe();
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', darkListener);
    };
  }, [selectedType, selectedStatus, selectedIndustry, selectedYear]);

  const colors = {
    bg: 'bg-bg-card dark:bg-nav',
    card: 'bg-role-admin/10 dark:bg-role-admin/90',
    text: 'text-gray-900 dark:text-gray-100',
    subtext: 'text-gray-900/60',
    border: 'border-gray-900 dark:border-gray-100',
    blue: 'var(--chart-blue)',
    teal: 'var(--chart-emerald)',
    grid: darkMode ? 'var(--color-muted)' : 'var(--bg-table-alt)',
    tooltipBg: darkMode ? 'var(--color-nav-bg)' : 'var(--bg-main)',
    tooltipBorder: darkMode ? 'var(--color-success)' : 'var(--color-primary)',
    tooltipText: darkMode ? 'var(--color-success)' : 'var(--color-primary)',
  };

  if (loading) {
    return (
      <div
        className={`flex h-64 items-center justify-center rounded-xl md:h-80 ${colors.bg} p-4 shadow-sm md:p-6`}
      >
        <div className="text-gray-900/60">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl ${colors.bg} p-4 shadow-sm transition-colors duration-300 md:p-6`}>
      <div className="mb-4 flex flex-wrap gap-2 md:gap-3">
        <select
          value={selectedType}
          onChange={e => setSelectedType(e.target.value)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        >
          {types.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={e => setSelectedStatus(e.target.value)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        >
          {statuses.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={selectedIndustry}
          onChange={e => setSelectedIndustry(e.target.value)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        >
          {industries.map(i => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
        <select
          value={selectedYear}
          onChange={e => setSelectedYear(e.target.value)}
          className="rounded border px-2 py-1 text-sm md:px-3 md:text-base"
        >
          {years.map(y => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <h3 className="mb-3 text-base font-medium text-gray-900 dark:text-gray-100 md:mb-4 md:text-lg">
        Application Trends
      </h3>
      <div className="h-64 md:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis
              dataKey="month"
              stroke={colors.blue}
              tick={{ fill: darkMode ? 'var(--bg-card)' : 'var(--text-primary)' }}
            />
            <YAxis
              stroke={colors.blue}
              tick={{ fill: darkMode ? 'var(--bg-card)' : 'var(--text-primary)' }}
            />
            <Tooltip
              contentStyle={{
                background: colors.tooltipBg,
                borderColor: colors.tooltipBorder,
                color: colors.tooltipText,
              }}
              labelStyle={{ color: colors.tooltipText }}
            />
            <Legend wrapperStyle={{ color: darkMode ? 'var(--bg-card)' : 'var(--text-primary)' }} />
            <Line
              type="monotone"
              dataKey="commercial"
              stroke={colors.blue}
              name="Commercial Applications"
              strokeWidth={3}
              dot={{ r: 4, fill: colors.blue }}
              activeDot={{ r: 6, fill: colors.blue }}
            />
            <Line
              type="monotone"
              dataKey="bif"
              stroke={colors.teal}
              name="BIF Applications"
              strokeWidth={3}
              dot={{ r: 4, fill: colors.teal }}
              activeDot={{ r: 6, fill: colors.teal }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setDarkMode(dm => !dm)}
          className="rounded-full bg-role-admin p-3 text-black shadow-lg transition-colors hover:bg-role-admin/80 dark:bg-role-admin dark:hover:bg-role-admin/80"
          aria-label="Toggle dark mode"
        >
          {darkMode ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default ApplicationsTrendChart;
